import type { Statements } from "@/lib/api";

const gbp = (v: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(v);

const cell = (v: number, strong = false) =>
  `<td class="num${v < 0 ? " neg" : ""}${strong ? " strong" : ""}">${
    v === 0 ? "–" : gbp(v)
  }</td>`;

const MONTH = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleString("en-GB", { month: "short", timeZone: "UTC" });

/**
 * Print-ready view of the three statements: opens a window with a clean
 * paper layout and triggers the browser's print dialog (→ Save as PDF).
 */
export function openStatementsPdf(s: Statements) {
  const months = s.pnl.months;
  const head = `<tr><th></th>${months.map((m) => `<th class="num">${MONTH(m)}</th>`).join("")}<th class="num">Total</th></tr>`;
  const line = (name: string, monthly: number[], total: number, cls = "") =>
    `<tr class="${cls}"><td>${name}</td>${monthly.map((v) => cell(v, cls === "total")).join("")}${cell(total, true)}</tr>`;
  const sum = (a: number[]) => Math.round(a.reduce((x, y) => x + y, 0) * 100) / 100;

  const pnlRows = [
    line("Income", s.pnl.incomeMonthly, s.pnl.totalIncome, "section"),
    ...s.pnl.income.map((l) => line(`&nbsp;&nbsp;${l.name}`, l.monthly, l.total)),
    line("Expenses", s.pnl.expenseMonthly, s.pnl.totalExpense, "section"),
    ...s.pnl.expense.map((l) => line(`&nbsp;&nbsp;${l.name}`, l.monthly, l.total)),
    line(
      "Net profit / (loss)",
      s.pnl.netMonthly,
      Math.round((s.pnl.totalIncome - s.pnl.totalExpense) * 100) / 100,
      "total",
    ),
  ].join("");

  const cf = s.cashflow;
  const cfRows = [
    line("Operating", cf.operatingMonthly, sum(cf.operatingMonthly), "section"),
    ...cf.operating.map((l) => line(`&nbsp;&nbsp;${l.name}`, l.monthly, l.total)),
    ...(cf.investing.length
      ? [
          line("Investing", cf.investingMonthly, sum(cf.investingMonthly), "section"),
          ...cf.investing.map((l) => line(`&nbsp;&nbsp;${l.name}`, l.monthly, l.total)),
        ]
      : []),
    ...(cf.financing.length
      ? [
          line("Financing", cf.financingMonthly, sum(cf.financingMonthly), "section"),
          ...cf.financing.map((l) => line(`&nbsp;&nbsp;${l.name}`, l.monthly, l.total)),
        ]
      : []),
    line("Net cash flow", cf.netMonthly, sum(cf.netMonthly), "total"),
    line(
      "Closing cash",
      cf.closingMonthly,
      cf.closingMonthly[cf.closingMonthly.length - 1] ?? 0,
      "total",
    ),
  ].join("");

  const b = s.balanceSheet;
  const bsRow = (name: string, v: number, cls = "") =>
    `<tr class="${cls}"><td>${name}</td>${cell(v, cls === "total")}</tr>`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Vernias — Financial statements ${s.year}</title>
<style>
  /* Print output is always light, so these are literal copies of the
     light-theme tokens (index.css) rather than var() reads — the print window
     is a fresh document with none of the app's stylesheets attached. */
  @page { size: A4 landscape; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font: 12px/1.5 "Georgia", "Times New Roman", serif; color: #16191b; margin: 0; }
  h1 { font-size: 22px; margin: 0 0 2px; }
  .meta { color: #6e767b; font-size: 11px; margin-bottom: 24px; }
  h2 { font-size: 15px; margin: 26px 0 8px; border-bottom: 2px solid #16191b; padding-bottom: 4px; }
  table { border-collapse: collapse; width: 100%; font-size: 10.5px; }
  th, td { padding: 4px 8px; text-align: left; border-bottom: 1px solid #e5e5e5; }
  th { font-size: 9px; text-transform: uppercase; letter-spacing: .06em; color: #6e767b; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .neg { color: #b4452f; }
  .section td { font-weight: 700; border-top: 1px solid #16191b; }
  .total td { font-weight: 700; background: #f7f7f7; }
  .strong { font-weight: 700; }
  .note { color: #6e767b; font-size: 10px; margin-top: 8px; font-style: italic; }
  .statement { page-break-inside: avoid; }
  .bs table { max-width: 420px; }
  .footer { margin-top: 28px; color: #6e767b; font-size: 9px; }
</style></head><body>
  <h1>Vernias Solutions Ltd</h1>
  <div class="meta">Financial statements ${s.year} · management accounts, cash basis, GBP · generated ${new Date().toLocaleDateString("en-GB")}</div>

  <div class="statement">
    <h2>Income statement</h2>
    <table>${head}${pnlRows}</table>
  </div>

  <div class="statement">
    <h2>Cash flow statement <span style="font-weight:400;font-size:11px">(direct method)</span></h2>
    <table>${head}${cfRows}</table>
    <p class="note">Opening cash ${gbp(cf.opening)} at 1 January ${s.year}.</p>
  </div>

  <div class="statement bs">
    <h2>Balance sheet <span style="font-weight:400;font-size:11px">(as at ${new Date().toLocaleDateString("en-GB")})</span></h2>
    <table>
      ${bsRow("Assets — cash at bank", b.cash, "section")}
      ${b.liabilities !== 0 ? bsRow("Liabilities", b.liabilities, "section") : ""}
      ${bsRow("Equity — contributed capital", b.contributed, "section")}
      ${bsRow("&nbsp;&nbsp;Retained earnings", b.retained)}
      ${bsRow("Total equity", b.equityTotal, "total")}
    </table>
    <p class="note">${b.balances ? "Assets = liabilities + equity ✓" : "Warning: statement out of balance"} ·
      accruals, receivables and depreciation are handled by the accountant at year-end.</p>
  </div>

  <div class="footer">Vernias Portal · internal management accounts — not statutory filings</div>
<script>window.onload = () => { window.print(); };</script>
</body></html>`;

  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}
